import { useCallback, useMemo, useRef, useState } from 'react';
import { getDefectIntelligence } from '../../modules/intelligence';
import { ThemeProvider } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { diTheme, exportTableToCSV, monoFont, isOpenDefect } from './DiConstants';
import { InsightsTab, DupesTab, computeInsights } from './DiInsightsDupes';
import { TableTab } from './DiTableTab';
import { useMutation, useQuery } from '@tanstack/react-query';
import MultiSelectDropdown from '../elements/MultiSelectDropdown';
import QUERY_KEYS from '../../query/queryKeys';
import { getProjectByRole } from '../../modules/project';

function UploadZone({ file, drag, onDragOver, onDragLeave, onDrop, onClick }) {
  const ready = !!file;
  const active = ready || drag;
  return (
    <Box
      onClick={onClick} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      sx={{
        backgroundColor: active ? '#ecfdf5' : '#ffffff',
        border: `1.5px ${ready ? 'solid' : 'dashed'} ${active ? '#059669' : '#d4d8e2'}`,
        borderRadius: '12px', p: '52px 40px', textAlign: 'center', cursor: 'pointer',
        transition: 'border-color 0.2s, background 0.2s', mb: 2.5,
        '&:hover': { borderColor: '#059669', backgroundColor: '#ecfdf5' },
      }}
    >
      <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, backgroundColor: ready ? '#ecfdf5' : '#f3f4f6', border: `1px solid ${ready ? '#a7f3d0' : '#e8eaef'}`, borderRadius: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22, fill: 'none', stroke: ready ? '#059669' : '#9ca3af', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </Box>
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#111827', mb: 0.75 }}>
        {file ? `✓  ${file.name}` : 'Drop your Jira Issues CSV here'}
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
        {file ? `${(file.size / 1024).toFixed(1)} KB — ready to classify` : 'or click to browse your files'}
      </Typography>
    </Box>
  );
}

function StatusToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, v) => { if (v) onChange(v); }}
      size="small"
      sx={{
        border: '1px solid #e8eaef', borderRadius: '8px', overflow: 'hidden',
        '& .MuiToggleButton-root': {
          textTransform: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 600, fontSize: 12, color: '#9ca3af', border: 'none',
          px: 2, py: 0.875,
          '&.Mui-selected': { backgroundColor: '#111827', color: '#ffffff', '&:hover': { backgroundColor: '#1f2937' } },
          '&:hover': { backgroundColor: '#f3f4f6' },
        },
      }}
    >
      <ToggleButton value="open">Open only</ToggleButton>
      <ToggleButton value="all">All issues</ToggleButton>
    </ToggleButtonGroup>
  );
}

export default function DefectIntelligence() {
  const [file, setFileState] = useState(null);
  const [drag, setDrag] = useState(false);
  const [tab, setTab] = useState(0);
  const [loadMsg, setLoadMsg] = useState('Classifying…');
  const [statusView, setStatusView] = useState('open');
  const [mounted, setMounted] = useState(new Set([0]));
  const fileRef = useRef();
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const { data: projectList } = useQuery({
    queryKey: QUERY_KEYS.project.projectsByRole(),
    queryFn: () => getProjectByRole(),
    enabled: true,
  });

  const pickFile = useCallback((f) => { if (f) setFileState(f); }, []);

  const { mutate: classify, data: result, isPending: loading, error, reset } = useMutation({
    mutationFn: async ({ file, selectedProjectIds }) => {
      setLoadMsg('Classifying…');
      const data = await getDefectIntelligence(file, selectedProjectIds);
      setLoadMsg('Building analytics…');
      return data;
    },
    onSuccess: () => setTab(0),
  });

  const handleClassify = () => { if (file) classify({ file, selectedProjectIds }); };

  const handleReset = () => {
    setFileState(null);
    reset();
    setTab(0);
    setMounted(new Set([0]));
    setStatusView('open');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleTabChange = (_, v) => {
    setTab(v);
    setMounted(prev => {
      if (prev.has(v)) return prev;
      const next = new Set(prev);
      next.add(v);
      return next;
    });
  };

  const filteredClassified = useMemo(() => {
    if (!result?.classified) return [];
    if (statusView === 'all') return result.classified;
    return result.classified.filter(isOpenDefect);
  }, [result, statusView]);

  const { filteredDupGroups, filteredIndexMap } = useMemo(() => {
    if (!result?.classified) return { filteredDupGroups: [], filteredIndexMap: new Map() };

    const filteredIndexMap = new Map();
    let fi = 0;
    for (let i = 0; i < result.classified.length; i++) {
      if (statusView === 'all' || isOpenDefect(result.classified[i])) {
        filteredIndexMap.set(i, fi++);
      }
    }

    const filteredDupGroups = (result.dup_groups || [])
      .map(grp => grp.filter(origIdx => filteredIndexMap.has(origIdx)).map(origIdx => filteredIndexMap.get(origIdx)))
      .filter(grp => grp.length >= 2);

    return { filteredDupGroups, filteredIndexMap };
  }, [result, statusView]);

  const computedInsights = useMemo(() => {
    if (!filteredClassified.length && !result) return null;
    return computeInsights(filteredClassified, filteredDupGroups);
  }, [filteredClassified, filteredDupGroups]);

  const openCount = useMemo(
    () => result?.classified?.filter(isOpenDefect).length ?? 0,
    [result],
  );
  const allCount = result?.classified?.length ?? 0;

  const projectOptions = useMemo(() => {
    return (
      projectList?.data?.map((p) => ({
        id: p.project_id,
        name: p.project_name,
      })) || []
    );
  }, [projectList]);

  return (
    <ThemeProvider theme={diTheme}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <Box sx={{ backgroundColor: '#f8f9fb', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
        <Box sx={{ maxWidth: 1350, mx: 'auto', px: 4, pt: 5, pb: 15 }}>
           <Box sx={{ mb: 4 }}>
            <Box sx={{ maxWidth: 500 }}>
              <MultiSelectDropdown
                options={projectOptions}
                selectedValues={selectedProjectIds}
                placeholder="Search and select projects..."
                onChange={setSelectedProjectIds}
                showSelectAll
                searchable
                name="projects"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8eaef', borderWidth: '1.5px' },
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d8e2' },
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#111827', borderWidth: '1.8px' },
                    }
                  },
                  '& .MuiSelect-select': {
                    py: 1.75,
                    fontSize: 13.5,
                    fontWeight: 500,
                    color: '#111827',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }
                }}
              />
            </Box>
          </Box>
          <UploadZone
            file={file} drag={drag}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files[0]); }}
          />
          <input
            ref={fileRef} type="file" accept=".csv"
            style={{ display: 'none' }}
            onChange={e => pickFile(e.target.files[0])}
          />
          
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap', mb: 5 }}>
            <Button
              variant="contained"
              disabled={!file || loading}
              onClick={handleClassify}
              sx={{ backgroundColor: '#111827', '&:hover': { backgroundColor: '#1f2937' }, px: 2.25, py: 1.125 }}
              startIcon={loading ? <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} /> : null}
            >
              {loading ? 'Running...' : 'Run'}
            </Button>
            {result && (
              <Button
                variant="outlined" onClick={handleReset}
                sx={{ borderColor: '#fecaca', color: '#dc2626', '&:hover': { backgroundColor: '#fef2f2', borderColor: '#fecaca' } }}
              >
                Reset
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px', fontSize: 13 }}>
              {error?.message || 'Classification failed'}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, backgroundColor: '#ffffff', border: '1px solid #e8eaef', borderRadius: '8px', mb: 3 }}>
              <CircularProgress size={16} sx={{ color: '#111827', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, color: '#374151' }}>{loadMsg}</Typography>
            </Box>
          )}

          {result && computedInsights && (
            <Box sx={{ animation: 'fadeUp 0.35s ease', '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'none' } } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2.5, py: 1.75, backgroundColor: '#ffffff', border: '1px solid #e8eaef', borderRadius: '12px', mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af' }}>
                  {allCount} total · {openCount} open · {computedInsights.duplicate_groups} duplicate clusters
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => exportTableToCSV(filteredClassified, filteredDupGroups)}
                    sx={{ borderColor: '#d4d8e2', color: '#374151', fontSize: 13, px: 2, py: 1, '&:hover': { backgroundColor: '#f3f4f6', borderColor: '#d4d8e2' } }}
                  >
                    Download CSV
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <StatusToggle value={statusView} onChange={setStatusView} />
                <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af' }}>
                  Showing {filteredClassified.length} issue{filteredClassified.length !== 1 ? 's' : ''}
                  {statusView === 'open' ? ' (open only)' : ''}
                </Typography>
              </Box>

              <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: '2px solid #e8eaef', mb: 4.5 }}>
                <Tab label="Insights" />
                <Tab label="Similar Issues" />
                <Tab label="All Issues" />
              </Tabs>

              <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
                {mounted.has(0) && <InsightsTab ins={computedInsights} />}
              </Box>
              <Box sx={{ display: tab === 1 ? 'block' : 'none' }}>
                {mounted.has(1) && (
                  <DupesTab
                    dups={filteredDupGroups}
                    classified={filteredClassified}
                  />
                )}
              </Box>
              <Box sx={{ display: tab === 2 ? 'block' : 'none' }}>
                {mounted.has(2) && (
                  <TableTab
                    classified={filteredClassified}
                    dupGroups={filteredDupGroups}
                  />
                )}
              </Box>

            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}